import { Component, computed, ElementRef, Inject, signal, Signal, viewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { LexiconModule } from '@galaxy/lexicon';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, Observable, of, switchMap, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { Conversation, MeetingAnalysisApiService, PrivacyLevel } from '@vendasta/meeting-analysis';
import { toSignal } from '@angular/core/rxjs-interop';
import { GalaxyThreePanelModule } from '@vendasta/galaxy/galaxy-three-panel';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { MatDialog } from '@angular/material/dialog';
import { ShareDialogComponent } from '../share-dialog/share-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GuestsComponent } from '../guests/guests.component';
import { ScoreBadgeComponent } from '../score-badge/score-badge.component';
import { AttendeesComponent } from '../attendees/attendees.component';
import { ParticipantsComponent } from '../participants/participants.component';
import { MeetingTranscriptComponent } from '../meeting-transcript/meeting-transcript.component';
import { SummaryComponent } from './summary/summary.component';
import { InsightsComponent } from './insights/insights.component';
import { VisibilityComponent } from '../../meeting-list/meeting-list-item/visibility/visibility.component';
import { StartAndEndComponent } from './start-and-end/start-and-end.component';
import { PageNotFound404Component } from '@vendasta/galaxy/page-not-found-404';
import { Context, MEETING_ANALYSIS_CONTEXT_INJECTION_TOKEN$ } from '@galaxy/meeting-analysis/static';

@Component({
  imports: [
    CommonModule,
    GalaxyPageModule,
    LexiconModule,
    GalaxyThreePanelModule,
    MatTabsModule,
    MatCardModule,
    MatChipsModule,
    GalaxyLoadingSpinnerModule,
    GalaxyEmptyStateModule,
    MatButtonModule,
    MatIconModule,
    GuestsComponent,
    ScoreBadgeComponent,
    AttendeesComponent,
    ParticipantsComponent,
    MeetingTranscriptComponent,
    SummaryComponent,
    InsightsComponent,
    VisibilityComponent,
    StartAndEndComponent,
    PageNotFound404Component,
  ],
  templateUrl: './meeting-page-v2.component.html',
  styleUrl: './meeting-page-v2.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class MeetingPageV2Component {
  videoPlayer = viewChild<ElementRef<HTMLMediaElement>>('videoPlayer');
  tabIndex = 0;
  Context = Context;

  private conversationId$: Observable<string | null> = this.activatedRoute.paramMap.pipe(
    map((params) => params.get('conversationId')),
  );

  timeStamp$: Observable<number | null> = this.activatedRoute.queryParamMap.pipe(
    map((params) => {
      const timeStamp = params.get('t');
      return timeStamp ? parseFloat(timeStamp) : 0.1;
    }),
  );
  private refreshConversation$ = new BehaviorSubject<void>(undefined);
  errorType = signal<'none' | 'not-found' | 'permission-denied'>('none');
  private conversation$: Observable<Conversation | null> = combineLatest([
    this.conversationId$,
    this.refreshConversation$,
  ]).pipe(
    switchMap(([conversationId, _]) => this.meetingAnalysisApiService.getConversation({ id: conversationId })),
    map((response) => {
      this.errorType.set('none');
      return response.conversation;
    }),
    catchError((error) => {
      if (error?.status === 403) {
        this.errorType.set('permission-denied');
      } else if (error?.status === 404) {
        this.errorType.set('not-found');
      } else {
        this.errorType.set('permission-denied');
      }
      this.snackbarService.openErrorSnack('COMMON.ERROR');
      return of(null);
    }),
  );

  conversation: Signal<Conversation | null | undefined> = toSignal<Conversation | null>(this.conversation$);
  endTime = computed(() => {
    const conversation = this.conversation();
    if (!conversation?.duration || !conversation?.meetingDate) return conversation?.meetingDate;

    const durationInMs = Math.floor(parseFloat(conversation.duration) * 1000);
    const meetingStartDate = new Date(conversation.meetingDate);
    return new Date(meetingStartDate.getTime() + durationInMs);
  });

  recordingURL$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.meetingAnalysisApiService.getRecordingUrl({ conversationId: conversationId })),
    map((response) => response?.recordingUrl),
    catchError(() => {
      this.snackbarService.openErrorSnack('MEETING_DETAILS.RECORDING.LOADING_ERROR');
      return '';
    }),
  );
  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly meetingAnalysisApiService: MeetingAnalysisApiService,
    private readonly snackbarService: SnackbarService,
    private location: Location,
    private dialog: MatDialog,
    @Inject(MEETING_ANALYSIS_CONTEXT_INJECTION_TOKEN$) protected readonly context: Context,
  ) {}

  onTimeStampSelected(seconds: number): void {
    if (this.videoPlayer()?.nativeElement) {
      this.videoPlayer().nativeElement.currentTime = seconds;
      this.videoPlayer().nativeElement.play();
    }
  }

  popShareModal(): void {
    const dialogResult = this.dialog.open(ShareDialogComponent, {
      width: '450px',
      data: {
        currentTimestamp: this.videoPlayer()?.nativeElement?.currentTime || 0,
        url: window.location.href.split('?')[0],
        namespace: this.conversation()?.namespace || '',
        privacyLevel: this.conversation()?.privacyLevel || PrivacyLevel.PRIVACY_LEVEL_OPEN,
        conversationId: this.conversation()?.id || '',
      },
    });
    dialogResult
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.refreshConversation$.next();
      });
  }
  navigateToPreviousPage() {
    this.location.back();
  }

  internalAttendees = computed(() => {
    const attendees = this.conversation()?.internalAttendees || [];
    return [...new Set(attendees)];
  });
  externalAttendeesContactIds = computed(() => {
    const conversation = this.conversation();
    return (
      (conversation?.externalAttendees || [])
        .map((attendee) => attendee.contactId)
        .filter((contactId) => !!contactId) || []
    );
  });

  switchToGuestsTab(): void {
    this.tabIndex = 2;
  }
}
